import { LoadingOverlay, Stack } from '@mantine/core';
import { useMemo, useRef } from 'react';
import type { CostVsBudgetChartResponse } from '../../../../api/financials-client/financials-client.type';
import { exportChart } from '../../../../helpers/image-downloader/image-downloader';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import { setCostVersusOutputChartType } from '../../../../store/financials-store/financials-store.actions';
import { ChartType } from '../../../../store/financials-store/financials-store.type';
import { secondaryBase } from '../../../../styles/design-tokens';
import { ChartDisplayTypeButtonSet } from '../shared/chart-display-type-button-set';
import { Chart as BarChart } from './bar-chart';
import { buildTrendLines, transformCostVersusOutputData } from './cost-versus-output.helpers';
import { Chart as LineChart } from './line-chart';

interface ChartProps {
  data?: CostVsBudgetChartResponse;
  isPending: boolean;
}

function Chart({ data, isPending }: ChartProps) {
  const selectedChartType = useFinancialsStore((state) => state.filters.costVersusOutput.selectedChartType);

  const chartRef = useRef<HTMLDivElement>(null!);

  const chartData = transformCostVersusOutputData(data);

  const chartDataWithTrendLines = buildTrendLines(chartData);

  const chart = useMemo(() => {
    if (selectedChartType === ChartType.Line) {
      return <LineChart chartData={chartDataWithTrendLines} chartRef={chartRef} />;
    }
    return <BarChart chartData={chartDataWithTrendLines} chartRef={chartRef} />;
  }, [selectedChartType, chartDataWithTrendLines]);

  return (
    <Stack h="100%" style={{ overflow: 'hidden', position: 'relative' }}>
      <LoadingOverlay visible={isPending} loaderProps={{ color: secondaryBase }} />
      <ChartDisplayTypeButtonSet
        onSetLineChart={() => setCostVersusOutputChartType(ChartType.Line)}
        onSetBarChart={() => setCostVersusOutputChartType(ChartType.Bar)}
        selectedChartType={selectedChartType}
        onExportChart={() => exportChart(chartRef.current, 'cost-versus-output-chart.png')}
      />
      {chart}
    </Stack>
  );
}

export { Chart };
